// Configuration system for application-specific business logic

export interface LibraryConfig {
  // Route and navigation configuration
  routes?: {
    // Function to generate paths with IDs
    generatePathWithId?: (path: string, id: string | number) => string;
    // Base paths for different modules
    basePaths?: {
      [module: string]: string;
    };
  };
  
  // Data transformation functions
  dataTransforms?: {
    // Function to transform API responses
    transformApiResponse?: (response: any, context?: string) => any;
    // Function to format currency
    formatCurrency?: (amount: number, currency?: string) => string;
    // Function to format dates
    formatDate?: (date: any, format?: string) => string;
  };
  
  // Validation functions
  validation?: {
    // Custom validation rules
    validateField?: (value: any, rules: any) => boolean | string;
    // Phone number validation
    validatePhone?: (phone: string, country?: string) => boolean;
    // Email validation
    validateEmail?: (email: string) => boolean;
  };
  
  // Permission system
  permissions?: {
    // Check if user has permission
    hasPermission?: (permission: string, context?: any) => boolean;
    // Get user permissions
    getUserPermissions?: () => string[];
  };
  
  // Localization
  localization?: {
    // Get current language
    getCurrentLanguage?: () => string;
    // Translate function
    translate?: (key: string, params?: any) => string;
    // Get available languages
    getAvailableLanguages?: () => Array<{ code: string; name: string }>;
  };
  
  // File handling
  fileHandling?: {
    // Upload file function
    uploadFile?: (file: File, options?: any) => Promise<any>;
    // Download file function
    downloadFile?: (fileId: string, options?: any) => Promise<any>;
    // Get file URL
    getFileUrl?: (fileId: string) => string;
  };
  
  // Notification system
  notifications?: {
    // Show success notification
    showSuccess?: (message: string, options?: any) => void;
    // Show error notification
    showError?: (message: string, options?: any) => void;
    // Show info notification
    showInfo?: (message: string, options?: any) => void;
  };
}

let libraryConfig: LibraryConfig = {};

export const configureLibrary = (config: LibraryConfig) => {
  libraryConfig = { ...libraryConfig, ...config };
};

export const getLibraryConfig = () => libraryConfig;

// Helper function to get configuration with fallback
export const getConfigFunction = <T extends keyof LibraryConfig>(
  category: T,
  functionName: keyof LibraryConfig[T]
): any => {
  const categoryConfig = libraryConfig[category];
  if (categoryConfig && categoryConfig[functionName]) {
    return categoryConfig[functionName];
  }
  
  // Return default implementation or throw error based on criticality
  switch (`${String(category)}.${String(functionName)}`) {
    case 'routes.generatePathWithId':
      return (path: string, id: string | number) => path.replace(':id', String(id));
    
    case 'dataTransforms.formatCurrency':
      return (amount: number, currency = 'USD') => `${currency} ${amount.toFixed(2)}`;
    
    case 'dataTransforms.formatDate':
      return (date: any) => new Date(date).toLocaleDateString();
    
    case 'validation.validateEmail':
      return (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    
    case 'permissions.hasPermission':
      return () => true; // Default to allowing all permissions
    
    case 'localization.getCurrentLanguage':
      return () => 'en';
    
    case 'localization.translate':
      return (key: string) => key; // Return key as fallback
    
    case 'notifications.showSuccess':
    case 'notifications.showError':
    case 'notifications.showInfo':
      return (message: string) => console.log(message);
    
    default:
      return (...args: any[]) => {
        throw new Error(`Configuration not provided for ${String(category)}.${String(functionName)}. Please configure this in your consuming application.`);
      };
  }
};